import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Edit3, X, Image as ImageIcon, Eye, EyeOff, GripVertical } from "lucide-react";
import MediaLibrary, { absMediaUrl } from "@/pages/admin/MediaLibrary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
    translations: {
        fr: { title: "", description: "", category: "" },
        nl: { title: "", description: "", category: "" },
        en: { title: "", description: "", category: "" },
    },
    media_ids: [],
    is_published: true,
    order: 0,
};

const LOCALE_LABELS = { fr: "Français", nl: "Nederlands", en: "English" };

export default function GalleryAdmin() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [locale, setLocale] = useState("fr");
    const [saving, setSaving] = useState(false);
    const [showPicker, setShowPicker] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/gallery-projects`, { withCredentials: true });
            setItems(data);
        } catch {
            toast.error("Chargement impossible.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openNew = () => setEditing({ ...EMPTY, translations: JSON.parse(JSON.stringify(EMPTY.translations)), order: items.length });
    const openEdit = (p) => {
        const tr = p.translations || {};
        setEditing({
            id: p.id,
            translations: {
                fr: { title: "", description: "", category: "", ...(tr.fr || {}) },
                nl: { title: "", description: "", category: "", ...(tr.nl || {}) },
                en: { title: "", description: "", category: "", ...(tr.en || {}) },
            },
            media_ids: p.media_ids || [],
            is_published: p.is_published,
            order: p.order || 0,
        });
        setLocale("fr");
    };
    const close = () => { setEditing(null); setShowPicker(false); };

    const setTr = (field, value) => {
        setEditing((e) => ({
            ...e,
            translations: {
                ...e.translations,
                [locale]: { ...e.translations[locale], [field]: value },
            },
        }));
    };

    const save = async () => {
        const fr = editing.translations.fr || {};
        if (!fr.title?.trim() || !fr.category?.trim()) {
            toast.error("Titre et catégorie FR requis (langue par défaut).");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                translations: editing.translations,
                media_ids: editing.media_ids || [],
                is_published: !!editing.is_published,
                order: Number(editing.order) || 0,
            };
            if (editing.id) {
                await axios.put(`${API}/admin/gallery-projects/${editing.id}`, payload, { withCredentials: true });
                toast.success("Projet mis à jour.");
            } else {
                await axios.post(`${API}/admin/gallery-projects`, payload, { withCredentials: true });
                toast.success("Projet créé.");
            }
            close();
            await load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Enregistrement impossible.";
            toast.error(typeof msg === "string" ? msg : "Enregistrement impossible.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement ce projet ?")) return;
        try {
            await axios.delete(`${API}/admin/gallery-projects/${id}`, { withCredentials: true });
            setItems((prev) => prev.filter((it) => it.id !== id));
            toast.success("Projet supprimé.");
        } catch {
            toast.error("Suppression impossible.");
        }
    };

    const togglePublish = async (p) => {
        try {
            await axios.put(
                `${API}/admin/gallery-projects/${p.id}`,
                {
                    translations: p.translations || {},
                    media_ids: p.media_ids || [],
                    is_published: !p.is_published,
                    order: p.order || 0,
                },
                { withCredentials: true },
            );
            await load();
        } catch {
            toast.error("Opération impossible.");
        }
    };

    const categoriesUsed = Array.from(new Set(items.map((i) => (i.translations?.fr?.category || "")).filter(Boolean))).sort();

    return (
        <section data-testid="gallery-admin" className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="overline text-[#64748B]">CMS</div>
                    <div className="font-serif text-2xl md:text-3xl">Galerie de réalisations</div>
                    <div className="text-xs text-[#64748B] mt-1">
                        {items.length} projet{items.length > 1 ? "s" : ""} · Catégories :{" "}
                        {categoriesUsed.length > 0 ? categoriesUsed.join(", ") : "—"}
                    </div>
                </div>
                <button
                    onClick={openNew}
                    data-testid="gallery-new-btn"
                    className="text-sm px-5 py-3 bg-[#1E3A5F] text-white hover:bg-[#152a45] transition-colors inline-flex items-center gap-2"
                >
                    <Plus size={16} strokeWidth={1.5} /> Nouveau projet
                </button>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-[#64748B]"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-slate-300 p-16 text-center text-[#64748B]">
                    Aucun projet. La galerie publique affiche les réalisations par défaut jusqu'à ce que vous en ajoutiez.
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((p) => (
                        <article
                            key={p.id}
                            data-testid={`gallery-project-${p.id}`}
                            className="bg-white border border-slate-200"
                        >
                            <div className="aspect-[4/3] bg-[#F4F2EE] overflow-hidden">
                                {p.images?.[0] ? (
                                    <img
                                        src={absMediaUrl(p.images[0].url)}
                                        alt={p.title}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-[#64748B]">
                                        <ImageIcon size={22} strokeWidth={1.2} />
                                    </div>
                                )}
                            </div>
                            <div className="p-5">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">{p.translations?.fr?.category || ""}</div>
                                        <div className="font-serif text-xl mt-1 truncate">{p.translations?.fr?.title || ""}</div>
                                        {p.translations?.fr?.description && (
                                            <div className="text-sm text-[#64748B] mt-1 line-clamp-2">{p.translations.fr.description}</div>
                                        )}
                                        <div className="text-xs text-[#64748B] mt-2">
                                            {p.images?.length || 0} image{(p.images?.length || 0) > 1 ? "s" : ""}
                                            {" · "}
                                            {["fr", "nl", "en"].filter((l) => p.translations?.[l]?.title).join(" · ").toUpperCase()}
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1 shrink-0">
                                        <button
                                            onClick={() => togglePublish(p)}
                                            className="p-2 text-[#64748B] hover:text-[#1E3A5F]"
                                            aria-label={p.is_published ? "Dépublier" : "Publier"}
                                            data-testid={`gallery-toggle-${p.id}`}
                                        >
                                            {p.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                                        </button>
                                        <button
                                            onClick={() => openEdit(p)}
                                            className="p-2 text-[#64748B] hover:text-[#1E3A5F]"
                                            aria-label="Modifier"
                                            data-testid={`gallery-edit-${p.id}`}
                                        >
                                            <Edit3 size={14} strokeWidth={1.5} />
                                        </button>
                                        <button
                                            onClick={() => remove(p.id)}
                                            className="p-2 text-[#64748B] hover:text-[#DC2626]"
                                            aria-label="Supprimer"
                                            data-testid={`gallery-delete-${p.id}`}
                                        >
                                            <Trash2 size={14} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {/* Modal */}
            <AnimatePresence>
                {editing && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-auto"
                        onClick={close}
                    >
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 20, opacity: 0 }}
                            className="bg-[#FDFBF7] w-full max-w-3xl border border-slate-200 my-8"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="gallery-edit-modal"
                        >
                            <div className="px-8 py-6 border-b border-slate-200 flex items-center justify-between">
                                <div className="font-serif text-2xl">
                                    {editing.id ? "Modifier le projet" : "Nouveau projet"}
                                </div>
                                <button onClick={close} className="p-2 text-[#64748B] hover:text-[#1E3A5F]" data-testid="gallery-modal-close">
                                    <X size={18} strokeWidth={1.5} />
                                </button>
                            </div>

                            <div className="px-8 py-6 space-y-6">
                                {/* Locale tabs */}
                                <div className="flex gap-2 border-b border-slate-200">
                                    {["fr", "nl", "en"].map((l) => (
                                        <button
                                            key={l}
                                            type="button"
                                            onClick={() => setLocale(l)}
                                            data-testid={`gallery-locale-${l}`}
                                            className={`text-xs uppercase tracking-[0.2em] px-4 py-3 border-b-2 transition-colors ${
                                                locale === l ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                                            }`}
                                        >
                                            {LOCALE_LABELS[l]}
                                            {editing.translations[l]?.title && <span className="ml-2 w-1.5 h-1.5 inline-block rounded-full bg-emerald-500" />}
                                        </button>
                                    ))}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                                    <div>
                                        <label className="overline text-[#64748B]">Titre {locale === "fr" ? "*" : ""}</label>
                                        <input
                                            className="field"
                                            value={editing.translations[locale]?.title || ""}
                                            onChange={(e) => setTr("title", e.target.value)}
                                            data-testid="gallery-field-title"
                                        />
                                    </div>
                                    <div>
                                        <label className="overline text-[#64748B]">Catégorie {locale === "fr" ? "*" : ""}</label>
                                        <input
                                            className="field"
                                            list="cat-suggestions"
                                            value={editing.translations[locale]?.category || ""}
                                            onChange={(e) => setTr("category", e.target.value)}
                                            placeholder="Cuisine, Rangement, Salon…"
                                            data-testid="gallery-field-category"
                                        />
                                        <datalist id="cat-suggestions">
                                            {categoriesUsed.map((c) => <option key={c} value={c} />)}
                                        </datalist>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="overline text-[#64748B]">Description</label>
                                        <textarea
                                            className="field resize-none"
                                            rows={3}
                                            value={editing.translations[locale]?.description || ""}
                                            onChange={(e) => setTr("description", e.target.value)}
                                            data-testid="gallery-field-description"
                                            placeholder="Localisation, matières, contexte…"
                                        />
                                    </div>
                                    <div>
                                        <label className="overline text-[#64748B]">Ordre d'affichage</label>
                                        <input
                                            type="number"
                                            className="field"
                                            value={editing.order}
                                            onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                                        />
                                    </div>
                                    <div className="flex items-end pb-2">
                                        <label className="inline-flex items-center gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={!!editing.is_published}
                                                onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                                                data-testid="gallery-field-published"
                                            />
                                            <span className="text-sm">Publié sur le site</span>
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="overline text-[#64748B]">
                                            Images ({editing.media_ids.length})
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setShowPicker((v) => !v)}
                                            data-testid="gallery-open-picker"
                                            className="text-xs uppercase tracking-[0.18em] px-3 py-2 border border-[#1E3A5F]/25 hover:bg-[#1E3A5F] hover:text-white transition-colors"
                                        >
                                            {showPicker ? "Fermer la bibliothèque" : "Sélectionner…"}
                                        </button>
                                    </div>
                                    {editing.media_ids.length > 0 ? (
                                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                                            {editing.media_ids.map((mid) => (
                                                <div key={mid} className="relative group aspect-square bg-[#F4F2EE]">
                                                    <img
                                                        src={absMediaUrl(`/api/media/${mid}`)}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setEditing({ ...editing, media_ids: editing.media_ids.filter((x) => x !== mid) })}
                                                        className="absolute top-1 right-1 p-1 bg-white/90 border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        aria-label="Retirer"
                                                        data-testid={`gallery-remove-image-${mid}`}
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="border border-dashed border-slate-300 p-8 text-center text-[#64748B] text-sm">
                                            Aucune image sélectionnée.
                                        </div>
                                    )}

                                    {showPicker && (
                                        <div className="mt-6 border border-slate-200 bg-white p-6 max-h-[500px] overflow-auto">
                                            <MediaLibrary
                                                selectable
                                                selectedIds={editing.media_ids}
                                                onSelect={(m) => {
                                                    if (editing.media_ids.includes(m.id)) {
                                                        setEditing({ ...editing, media_ids: editing.media_ids.filter((x) => x !== m.id) });
                                                    } else {
                                                        setEditing({ ...editing, media_ids: [...editing.media_ids, m.id] });
                                                    }
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="px-8 py-5 border-t border-slate-200 flex items-center justify-end gap-3">
                                <button onClick={close} className="text-sm px-5 py-3 border border-[#1E3A5F]/25 hover:bg-slate-100">
                                    Annuler
                                </button>
                                <button
                                    onClick={save}
                                    disabled={saving}
                                    data-testid="gallery-save"
                                    className="text-sm px-5 py-3 bg-[#1E3A5F] text-white hover:bg-[#152a45] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                                >
                                    {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                                    {editing.id ? "Enregistrer" : "Créer le projet"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
